/* Hook de chat IA — suporta modo SOAP + contexto plantão + multimodal */
import { useEffect, useState, useCallback, useRef } from 'react';
import { sendMessage, type SendMessageOptions } from '../api/chat';
import type { ChatMessage, ChatState, ChatError, AIMode, AttachmentFile } from '../types';

/** Gera ID único para mensagem */
function generateId(): string {
  return `msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Gera sessionId UUID v4 simples */
function generateSessionId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/* ─────────────────────────────────────────────────────────────────────────
   Persistência em localStorage (BUG FIX 20/05/2026 — refresh perdia memória)

   Backend mantém histórico em KV `${userId}:${sessionId}` por 12h. Antes
   do fix: `useRef(generateSessionId())` criava sessionId NOVO a cada
   montagem → refresh resultava em nova session_id → KV lookup falhava →
   IA esquecia tudo.

   Solução: persiste sessionId + mensagens em localStorage. Refresh recupera
   ambos. Backend já tem o restante.

   Cuidado com tamanho: attachments base64 podem inflate localStorage (5MB
   limit). Estratégia:
     - Limita a últimas MAX_PERSISTED_MESSAGES (30)
     - Remove base64 dos attachments antes de persistir (médico vê texto +
       "[imagem]" placeholder; backend já processou a imagem real)
   ──────────────────────────────────────────────────────────────────────── */

const STORAGE_KEY = 'drpera-ia-chat-state-v1';
// 20/05/2026: aumentado 30 → 80 — cobre plantão típico de 12h com 80 pacientes
// onde médico faz ≥1 troca user↔IA por paciente. Custo localStorage com base64
// strippado: ~500 bytes/msg × 80 = ~40KB total (zero risco de estourar 5MB).
const MAX_PERSISTED_MESSAGES = 80;

interface PersistedChatState {
  sessionId: string;
  messages: ChatMessage[];
  savedAt: number;
}

/**
 * TTL do estado persistido — alinhado com KV TTL backend (12h).
 * 02/07/2026: 36h → 12h, acompanhando a consolidação da memória do worker
 * com o plantão de 12h. Sem isso o front mostraria histórico que o backend
 * já esqueceu.
 */
const PERSIST_TTL_MS = 12 * 60 * 60 * 1000;

function loadPersistedChatState(): PersistedChatState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedChatState>;
    if (
      typeof parsed.sessionId !== 'string' ||
      !Array.isArray(parsed.messages) ||
      typeof parsed.savedAt !== 'number'
    ) {
      return null;
    }
    // Expirou? Backend KV TTL é 12h — se passar disso, sessão é inútil.
    if (Date.now() - parsed.savedAt > PERSIST_TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return {
      sessionId: parsed.sessionId,
      messages: parsed.messages,
      savedAt: parsed.savedAt,
    };
  } catch {
    return null;
  }
}

function persistChatState(sessionId: string, messages: ChatMessage[]): void {
  try {
    // Strip base64 dos attachments — preserva contexto visual ("[imagem]" placeholder)
    // sem inflar localStorage. IA backend já processou as imagens reais.
    const lean: ChatMessage[] = messages.slice(-MAX_PERSISTED_MESSAGES).map((m) => {
      if (m.attachments && m.attachments.length > 0) {
        return {
          ...m,
          attachments: m.attachments.map((att) => ({
            ...att,
            base64: '', // strip — economiza localStorage
          })),
        };
      }
      return m;
    });
    const payload: PersistedChatState = {
      sessionId,
      messages: lean,
      savedAt: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // localStorage full / quota / disabled — perda graciosa
  }
}

function clearPersistedChatState(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export interface UseChatOptions {
  /** Modo da IA: 'chat' | 'prontuario' */
  mode: AIMode;
  /** Contexto do plantão ativo (injetado no backend) */
  plantaoContext: string | null;
  /**
   * Trial v2: UUID v4 anônimo — só quando NÃO logado E flag trial_v2 ligada.
   * null (default) = comportamento atual exato (sem headers X-Anon-Id).
   */
  trialAnonId?: string | null;
}

export interface UseChatReturn extends ChatState {
  /**
   * Envia nova mensagem do usuário (com possíveis anexos).
   * `options` (09/07/2026): campos aditivos do body — ex. docRequest da
   * barra "Gerar documento". Omitido = request idêntico ao atual.
   */
  sendUserMessage: (
    content: string,
    attachments?: AttachmentFile[],
    options?: SendMessageOptions,
  ) => Promise<void>;
  /**
   * Insere mensagem LOCAL do Dr. Pera (template i18n — trial v2).
   * Exibida como mensagem do assistant mas NUNCA enviada ao backend.
   */
  appendLocalMessage: (content: string) => void;
  /** Limpa histórico e inicia nova sessão */
  clearSession: () => void;
  /** Limpa erro atual */
  clearError: () => void;
}

export function useChat(options: UseChatOptions): UseChatReturn {
  // BUG FIX 20/05/2026 — carrega sessionId + messages do localStorage se
  // existirem. Antes: refresh perdia tudo (sessionId novo, KV miss).
  const persisted = loadPersistedChatState();
  const sessionIdRef = useRef<string>(persisted?.sessionId ?? generateSessionId());

  const [state, setState] = useState<ChatState>({
    messages: persisted?.messages ?? [],
    sessionId: sessionIdRef.current,
    loading: false,
    error: null,
  });

  // Auto-save a cada mudança de mensagens — mantém localStorage sincronizado
  // com state. Usa sessionId atual (ref, não muda entre renders).
  useEffect(() => {
    if (state.messages.length === 0) return;
    persistChatState(sessionIdRef.current, state.messages);
  }, [state.messages]);

  const sendUserMessage = useCallback(
    async (
      content: string,
      attachments?: AttachmentFile[],
      // Nome distinto de `options` (UseChatOptions do hook) — evita shadowing
      sendOptions?: SendMessageOptions,
    ) => {
      if (!content.trim() && (!attachments || attachments.length === 0)) return;

      // Adiciona mensagem do usuário imediatamente na UI
      const userMsg: ChatMessage = {
        id: generateId(),
        role: 'user',
        content: content.trim(),
        attachments,
        timestamp: new Date().toISOString(),
      };

      setState((prev) => ({
        ...prev,
        messages: [...prev.messages, userMsg],
        loading: true,
        error: null,
      }));

      // Prepara histórico para o backend — monta content blocks multimodal.
      // FIX 24/05/2026 (Dr. Lu, bug brickou PC da UPA):
      // PDFs DEVEM ir como type:'document' (não 'image'). Anthropic Claude
      // rejeita 'application/pdf' como media_type de imagem com HTTP 400
      // invalid_request_error, e a mensagem corrompida fica no histórico KV
      // pelo TTL inteiro (12h hoje; 36h na época), brickando o usuário.
      // Antes a iaweb mandava TUDO como image —
      // mesmo bug que a extensão tinha antes do fix 20/05.
      // Trial v2: mensagens locais (kind='local' — templates i18n do frontend)
      // JAMAIS entram no histórico enviado ao backend/LLM.
      //
      // FIX 09/07/2026 (ordem do Dr. Lu — contexto duplicado): o cliente
      // manda SÓ a mensagem nova. A memória do SERVIDOR (KV, 12h, 60 msgs)
      // é a fonte de verdade do contexto — antes reenviávamos o histórico
      // inteiro e o backend anteponía a cópia dele, dobrando os tokens de
      // cada consulta. O localStorage continua existindo apenas pra RENDER
      // da conversa na UI (transcript visível), nunca mais vai pro modelo.
      const allMessages = [userMsg].filter((m) => m.kind !== 'local');
      const historyForApi = allMessages.map((m) => {
        if (m.role === 'user' && m.attachments && m.attachments.length > 0) {
          // Monta array de content blocks: texto + imagens + PDFs
          type ImageBlock = {
            type: 'image';
            source: { type: 'base64'; media_type: string; data: string };
          };
          type DocumentBlock = {
            type: 'document';
            source: { type: 'base64'; media_type: 'application/pdf'; data: string };
          };
          const blocks: Array<
            | { type: 'text'; text: string }
            | ImageBlock
            | DocumentBlock
          > = [];

          if (m.content) {
            blocks.push({ type: 'text', text: m.content });
          }

          for (const att of m.attachments) {
            // PDF → type:'document' (Anthropic docs)
            if (att.mediaType === 'application/pdf') {
              blocks.push({
                type: 'document',
                source: {
                  type: 'base64',
                  media_type: 'application/pdf',
                  data: att.base64,
                },
              });
            } else {
              // Imagem (png/jpeg/webp/gif) → type:'image'
              blocks.push({
                type: 'image',
                source: {
                  type: 'base64',
                  media_type: att.mediaType,
                  data: att.base64,
                },
              });
            }
          }

          return { role: m.role as 'user' | 'assistant', content: blocks };
        }
        return { role: m.role as 'user' | 'assistant', content: m.content };
      });

      try {
        const responseText = await sendMessage(
          historyForApi,
          sessionIdRef.current,
          options.mode,
          options.plantaoContext,
          options.trialAnonId ?? null,
          sendOptions,
        );

        const assistantMsg: ChatMessage = {
          id: generateId(),
          role: 'assistant',
          content: responseText,
          timestamp: new Date().toISOString(),
        };

        setState((prev) => ({
          ...prev,
          messages: [...prev.messages, assistantMsg],
          loading: false,
          error: null,
        }));
      } catch (err) {
        const chatError: ChatError = (() => {
          if (err && typeof err === 'object' && 'type' in err) {
            return err as ChatError;
          }
          return {
            type: 'unknown' as const,
            message: err instanceof Error ? err.message : 'Erro desconhecido',
          };
        })();

        setState((prev) => ({
          ...prev,
          loading: false,
          error: chatError,
        }));
      }
    },
    // state.messages saiu das deps: o payload agora leva só a mensagem nova
    // (fix 09/07/2026 — memória do servidor é a fonte de contexto).
    [options.mode, options.plantaoContext, options.trialAnonId],
  );

  /**
   * Trial v2 — insere mensagem local do Dr. Pera (reforço dia 3, sucesso do
   * email etc). kind='local' garante que nunca vai pro backend (filtro acima).
   */
  const appendLocalMessage = useCallback((content: string) => {
    if (!content.trim()) return;
    const localMsg: ChatMessage = {
      id: generateId(),
      role: 'assistant',
      kind: 'local',
      content,
      timestamp: new Date().toISOString(),
    };
    setState((prev) => ({
      ...prev,
      messages: [...prev.messages, localMsg],
    }));
  }, []);

  const clearSession = useCallback(() => {
    sessionIdRef.current = generateSessionId();
    // Limpa também localStorage — clearSession significa "começar fresco".
    clearPersistedChatState();
    setState({
      messages: [],
      sessionId: sessionIdRef.current,
      loading: false,
      error: null,
    });
  }, []);

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  return {
    ...state,
    sessionId: sessionIdRef.current,
    sendUserMessage,
    appendLocalMessage,
    clearSession,
    clearError,
  };
}
